import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { baseApi } from "@/shared/api";
import { setCompleteness, setError, setLoading } from "@/features/hr/hiring/slice";
import type { ApplicationType, CompletenessResponse, ValidateTokenResponse, DraftData } from "../types";

const COMPLETENESS_LOAD_ERROR_KEY = "page.error_message";

const EMPTY_COMPLETENESS: CompletenessResponse = {
  is_complete: false,
  overall_progress: 0,
  sections: {
    personal_data: { complete: false, missing_fields: [], missing_files: [] },
    contacts: { complete: false, missing_fields: [], missing_files: [] },
    addresses: { complete: false, missing_fields: [], missing_files: [] },
    id_documents: { complete: false, missing_fields: [], missing_files: [] },
    banking: { complete: false, missing_fields: [], missing_files: [] },
  },
};

export interface SaveApplicationDraftRequest {
  [key: string]: unknown;
}

export interface SaveApplicationDraftResponse {
  message: string;
  application_id: number;
  stage: string;
  status: string;
}

export interface SubmitApplicationResponse {
  success: boolean;
  message: string;
  application_id: number;
  stage?: string;
  status?: string;
  submitted_at?: string;
  application_type?: ApplicationType;
  credentials_sent?: boolean;
  email?: string;
  organization_id?: number;
}

export interface SubmitApplicationError {
  error: string;
  missing_sections?: string[];
  message?: string;
}

export const applyApplicationApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCompleteness: builder.query<CompletenessResponse, string>({
      query: (token) => `/api/hr/apply/${token}/completeness/`,
      providesTags: ["Completeness"],
      async onQueryStarted(token, { dispatch, queryFulfilled }) {
        try {
          dispatch(setLoading());
          const { data } = await queryFulfilled;
          dispatch(setCompleteness(data));
        } catch (err) {
          const error = err as { error: FetchBaseQueryError };
          if (error?.error?.status === 404) {
            dispatch(setCompleteness(EMPTY_COMPLETENESS));
            return;
          }
          dispatch(setError(COMPLETENESS_LOAD_ERROR_KEY));
        }
      },
    }),
    validateToken: builder.query<ValidateTokenResponse, string>({
      query: (token) => `/api/hr/validate-token/${token}/`,
    }),
    saveApplicationDraft: builder.mutation<
      SaveApplicationDraftResponse,
      { token: string; body: SaveApplicationDraftRequest }
    >({
      query: ({ token, body }) => ({
        url: `/api/hr/apply/${token}/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Completeness"],
    }),
    submitApplication: builder.mutation<SubmitApplicationResponse, string>({
      query: (token) => ({
        url: `/api/hr/apply/${token}/submit/`,
        method: "POST",
      }),
    }),
  }),
});

export const {
  useGetCompletenessQuery: useApplyApplicationGetCompletenessQuery,
  useValidateTokenQuery: useApplyApplicationValidateTokenQuery,
  useSaveApplicationDraftMutation: useApplyApplicationSaveDraftMutation,
  useSubmitApplicationMutation: useApplyApplicationSubmitMutation,
} = applyApplicationApi;

export type { CompletenessResponse, ValidateTokenResponse, DraftData };
